import React, { useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { zodResolver } from '@hookform/resolvers/zod';
import { endOfDay, format, parse, startOfDay, addDays } from 'date-fns';

import { Box } from '@mui/system';
import { Dialog, IconButton, Typography, Stack } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';

import { Button as SpectrumButton } from '@bestdoctor/spectrum-uikit/Button';

import { modalTitleStyle, modalSubtitleStyle } from 'ui/modals/modalGlobal';
import { dialogDetachPatientModalStyled } from 'ui/modals/modalDetach/modalDetach';
import { modalFormInputStyle } from 'ui/modals/modalGlobal';

import { useDetachPatients } from 'hooks/patients/useDetachPatients';

import { DetachmentPatientPassModalProps } from 'interfaces/patients/patientInterfaces';
import { DetachPatientsHookResponse } from 'interfaces/hooks/patientsHooksInterfaces';

import { DataPeriod, ErrorData } from 'interfaces/global/globalInterfaces';

import { useGetPeriodStaffAction } from 'hooks/periods/useGetPeriodStaffAction';
import { sessionStorageGetItem } from 'utils/sessionStorageGetItem';
import { disabledDates } from 'utils/dateHelpers';
import { getDetachErrorFeedback, getErrorResponse } from 'utils/getDetachErrorFeedback';
import {
  DateDetachParams,
  UNAVAILABLE_DETACH_DATE_MESSAGE,
  resolveDetachDateChange,
  validationDateDetach,
} from './validationDetach';

export const DetachPatientModal: React.FC<DetachmentPatientPassModalProps> = ({
  open,
  massDetach,
  patientData,
  selectedStaff,
  isEditDetachModal,
  detachDate,
  handleClose,
  onSubmit,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [dateDetachPatient, setDateDetachPatient] = useState<string | undefined>(undefined);

  // Минимальная дата - завтрашний день
  const minDate = useMemo(() => {
    return addDays(new Date(), 1);
  }, []);

  const mainFilterPeriodStaff = sessionStorageGetItem<DataPeriod>('mainFilterPeriodStaff');

  const { periodStaffAction } = useGetPeriodStaffAction({
    period: mainFilterPeriodStaff,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const disabledDays = (date: Date): boolean => {
    const actualDayTimeStamp = startOfDay(date).getTime();
    const minDateTimestamp = startOfDay(minDate).getTime();

    // Блокируем даты до завтрашнего дня
    if (actualDayTimeStamp < minDateTimestamp) {
      return true;
    }

    const canDetachSinceTimestamp = periodStaffAction
      ? startOfDay(parse(periodStaffAction.canDetachSince, 'yyyy-MM-dd', new Date())).getTime()
      : null;
    const canDetachSinceTimestampMax = periodStaffAction
      ? endOfDay(parse(periodStaffAction.canAttachOrDetachMaxDate, 'yyyy-MM-dd', new Date())).getTime()
      : null;
    if (disabledDates.some((item) => item === date.getTime())) {
      return disabledDates.some((item) => item === date.getTime());
    }

    if (canDetachSinceTimestamp && canDetachSinceTimestampMax) {
      return actualDayTimeStamp < canDetachSinceTimestamp || actualDayTimeStamp > canDetachSinceTimestampMax;
    }
    if (canDetachSinceTimestamp) {
      return actualDayTimeStamp < canDetachSinceTimestamp;
    }
    if (canDetachSinceTimestampMax) {
      return actualDayTimeStamp > canDetachSinceTimestampMax;
    }
    return false;
  };

  const {
    setValue,
    control,
    formState: { errors },
    setError,
    clearErrors,
    handleSubmit,
    watch,
  } = useForm<DateDetachParams>({
    mode: 'onTouched',
    resolver: zodResolver(validationDateDetach),
  });

  const selectedDetachDate = watch('detach_date');

  const resetForm = (): void => {
    setDateDetachPatient(undefined);
    clearErrors('detach_date');
    setValue('detach_date', '', { shouldDirty: true });
  };

  const showDetachError = async (response?: Response): Promise<void> => {
    const { message, fieldErrors, shouldRefreshList } = await getDetachErrorFeedback(response);

    enqueueSnackbar(message, { variant: 'error' });
    fieldErrors.forEach((item) => {
      setError('detach_date', {
        type: 'custom',
        message: item.message,
      });
    });

    if (shouldRefreshList) {
      queryClient.invalidateQueries('dataCompanyPatients');
    }
  };

  const { handleDetachPatients, isLoadDetachPatients } = useDetachPatients({
    massDetach,
    patient: patientData || null,
    selectedPatients: selectedStaff && !!selectedStaff?.length ? selectedStaff : null,

    onSuccess: async (response: DetachPatientsHookResponse): Promise<void> => {
      if (response.res.ok) {
        enqueueSnackbar(response.message, { variant: 'success' });
        queryClient.invalidateQueries('dataCompanyPatients');
        resetForm();
        handleClose();
        return;
      }

      await showDetachError(response.res);
    },
    onError: (error: unknown) => {
      void showDetachError(getErrorResponse(error));
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetForm();
        handleClose();
      }}
      sx={dialogDetachPatientModalStyled}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h3" sx={modalTitleStyle}>
            Открепление пациентов
          </Typography>
          {isEditDetachModal && detachDate ? (
            <Typography variant="subtitle1" sx={modalSubtitleStyle}>
              Обратите внимание, что пациент уже подан на открепление c {format(new Date(detachDate), 'dd.MM.yyyy')},
              повторное действие скорректирует дату откреплениям без отображения в документах
            </Typography>
          ) : (
            <>
              {' '}
              <Typography variant="subtitle1" sx={modalSubtitleStyle}>
                Статус для выбранных пациентов изменится на “Завершенный”, оказание услуг будет остановлено.
              </Typography>
              <Typography variant="subtitle1" sx={modalSubtitleStyle}>
                Для завершения открепления выберите дату, с которой оно вступит в силу.
              </Typography>
              {periodStaffAction?.relativesInsurance?.autoRelativeDetach?.value === 'enabled' && (
                <Typography variant="subtitle1" sx={modalSubtitleStyle}>
                  При откреплении застрахованного сотрудника, его родственники будут откреплены.
                </Typography>
              )}
            </>
          )}
        </Box>
        <IconButton
          aria-label="close modal"
          sx={{ width: '40px', height: '40px' }}
          onClick={() => {
            resetForm();
            handleClose();
          }}
        >
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        onSubmit={handleSubmit(handleDetachPatients)}
      >
        <Box sx={{ ...modalFormInputStyle(false), marginTop: '8px' }}>
          <style>
            {`
              [data-radix-popper-content-wrapper] {
                z-index: 1400 !important;
              }
            `}
          </style>
          <Controller
            control={control}
            name="detach_date"
            render={({ field: { onChange } }) => (
              <DatePicker
                mode="single"
                label="Укажите дату открепления"
                placeholder="Укажите дату открепления"
                value={dateDetachPatient}
                onChange={(newValue) => {
                  const result = resolveDetachDateChange(newValue || undefined, disabledDays);
                  setDateDetachPatient(result.kind === 'empty' ? undefined : result.displayDate);

                  if (result.kind === 'valid') {
                    clearErrors('detach_date');
                    onChange(result.formDate);
                    setValue('detach_date', result.formDate, { shouldDirty: true, shouldValidate: true });
                    return;
                  }

                  if (result.kind === 'unavailable') {
                    onChange('');
                    setValue('detach_date', '', { shouldDirty: true });
                    setError('detach_date', {
                      type: 'custom',
                      message: UNAVAILABLE_DETACH_DATE_MESSAGE,
                    });
                    return;
                  }

                  clearErrors('detach_date');
                  onChange('');
                  setValue('detach_date', '', { shouldDirty: true });
                }}
                disabled={isLoadDetachPatients}
                error={!!errors.detach_date?.message}
                helperProps={{
                  hint: errors.detach_date?.message || '',
                  textColor: {
                    surface: 'light',
                    variant: errors.detach_date?.message ? 'error' : 'secondary',
                  },
                }}
                required
                size="m"
                calendarProps={{
                  disabled: (date: Date) => disabledDays(date),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ marginTop: '28px', alignSelf: 'flex-end' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <SpectrumButton
              variant="pseudo"
              onClick={() => {
                resetForm();
                handleClose();
              }}
            >
              Отмена
            </SpectrumButton>
            <SpectrumButton
              variant="promo"
              type="submit"
              disabled={!selectedDetachDate || !!errors.detach_date || isLoadDetachPatients}
              onClick={onSubmit}
            >
              Открепить
            </SpectrumButton>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};
